import { type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSegment } from '@repo/poly-hooks/segment';
import { formatMoney } from '@repo/design-system-kit/utils/format-money';
import { Disclaimer } from '@repo/design-system-kit/components/disclaimer/disclaimer';
import { ExternalLink } from '@repo/design-system-kit/components/external-link/external-link';
import {
  StripeCardForm as KitStripeCardForm,
  type StripeCardFormProps as KitStripeCardFormProps,
} from '@repo/company-creation-kit/common/components/stripe-card-form/stripe-card-form';
import { createRegisterPartnersPaymentIntentAdapter } from '@repo/company-creation-kit/common/components/stripe-card-form/register-partners-create-payment-intent-adapter';

export interface RegisterPartnersPromoOffer {
  monthDuration: number;
  discountedPrice: number;
}

export type RegisterPartnersStripeCardFormProps = Omit<
  KitStripeCardFormProps,
  'paymentIntentAdapter' | 'onPaymentSubmit' | 'children' | 'termsCheckbox'
> & {
  registrationId: string;
  /** `annual` or `monthly` recurrence of the selected price plan. */
  recurrence: 'annual' | 'monthly';
  /** Regular (non-promotional) price of the selected plan, in EUR. */
  regularPrice: number;
  /** Name of the selected price plan (e.g. brand name), used in the disclaimer copy. */
  brandName: string;
  /** Present when a promotional offer applies to the selected price plan. */
  promoOffer?: RegisterPartnersPromoOffer;
};

/**
 * register-partners-js wrapper around the company-creation-kit `StripeCardForm`.
 *
 * Owns everything that must be built as a React node before reaching the kit
 * form: the recurrence/promo-dependent disclaimer copy and the terms &
 * conditions checkbox label (which contains links). Both were previously
 * computed in the Ember `payment` controller/template; they move here so the
 * Ember → React bridge only carries plain, serializable props.
 *
 * The payment subtitle and the order-summary block are NOT part of this
 * component — they remain Ember-owned, unchanged.
 */
export function RegisterPartnersStripeCardForm({
  registrationId,
  recurrence,
  regularPrice,
  brandName,
  promoOffer,
  ...props
}: RegisterPartnersStripeCardFormProps): ReactNode {
  const segment = useSegment();
  const { formatMessage } = useIntl();

  const paymentIntentAdapter = createRegisterPartnersPaymentIntentAdapter({ registrationId });

  const handlePaymentSubmit = (): void => {
    segment.track('onboarding_partner_payment_requested');
  };

  const disclaimer = buildDisclaimer({
    recurrence,
    regularPrice,
    brandName,
    promoOffer,
    formatMessage,
  });

  const termsAndConditionsLinks = (
    <FormattedMessage
      id="order.checks.terms-privacy.disclaimer"
      values={{
        termsLink: (
          <ExternalLink href={formatMessage({ id: 'order.checks.terms-privacy.terms.url' })}>
            <FormattedMessage id="order.checks.terms-privacy.terms.link-text" />
          </ExternalLink>
        ),
        privacyLink: (
          <ExternalLink href={formatMessage({ id: 'order.checks.terms-privacy.privacy.url' })}>
            <FormattedMessage id="order.checks.terms-privacy.privacy.link-text" />
          </ExternalLink>
        ),
      }}
    />
  );

  const termsCheckbox = {
    label: <FormattedMessage id="order.checks.terms-privacy.consent" />,
    errorMessage: formatMessage({
      id: 'sign_up.contract.deposit.terms-and-conditions.error',
    }),
  };

  return (
    <KitStripeCardForm
      {...props}
      onPaymentSubmit={handlePaymentSubmit}
      paymentIntentAdapter={paymentIntentAdapter}
      termsCheckbox={termsCheckbox}
    >
      <Disclaimer.Block className="mb-32" data-test-registrations-payment-disclaimer level="info">
        {disclaimer}
      </Disclaimer.Block>
      <p className="body-standard mb-32" data-test-registrations-payment-terms-links>
        {termsAndConditionsLinks}
      </p>
    </KitStripeCardForm>
  );
}

function buildDisclaimer({
  recurrence,
  regularPrice,
  brandName,
  promoOffer,
  formatMessage,
}: {
  recurrence: RegisterPartnersStripeCardFormProps['recurrence'];
  regularPrice: number;
  brandName: string;
  promoOffer: RegisterPartnersPromoOffer | undefined;
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
}): string {
  const regularAmount = formatMoney({ amount: regularPrice, token: 'money-symbol-compact' });

  if (promoOffer) {
    if (recurrence === 'annual') {
      return formatMessage(
        { id: 'price_plan.company-creators.check-out.total-amount-promo' },
        { regular_amount: regularAmount }
      );
    }

    return formatMessage(
      { id: 'order-new.disclaimer.monthly-promo' },
      {
        discount_duration: promoOffer.monthDuration,
        regular_amount: regularAmount,
        discounted_amount: formatMoney({
          amount: promoOffer.discountedPrice,
          token: 'money-symbol-compact',
        }),
        name: brandName,
      }
    );
  }

  if (recurrence === 'annual') {
    return formatMessage({ id: 'price_plan.company-creators.check-out.total-amount' });
  }

  return formatMessage(
    { id: 'order-new.disclaimer.monthly' },
    { name: brandName, amount: regularAmount }
  );
}
